import { Component, Injector, Input, OnChanges, SimpleChanges } from '@angular/core';
import { FormControl, FormGroup } from '@angular/forms';
import { distinctUntilChanged } from 'rxjs/operators';
import { FcItemListBaseComponent, FcListValidationService } from '../../generic';
import { ApAlertService } from 'webcommon/core-ui';
import { FcItemModalContext } from '../../../models';
import { FcFacilityPortalPersonListItemDto, FormGroupValidationRules } from 'webcommon/shared';

@Component({
  providers: [ApAlertService, FcListValidationService],
  selector: 'fc-other-information',
  templateUrl: './fc-other-information.component.html',
  styleUrls: ['./fc-other-information.component.less']
})
export class FcOtherInformationComponent extends FcItemListBaseComponent implements OnChanges {
  @Input() baseId: string;
  @Input() form: FormGroup;
  @Input() listItem: FcFacilityPortalPersonListItemDto;
  @Input() isSubmitted = false;
  @Input() itemContext: FcItemModalContext;


  public birthMaxDate?: Date;
  public deathMinDate?: Date;
  public deathMaxDate = new Date();

  public genderUidControl: FormControl = new FormControl<string[]>([]);
  public birthGenderUidControl: FormControl = new FormControl<string[]>([]);
  public genderIdentityUidControl: FormControl = new FormControl<string[]>([]);
  public sexualOrientationUidControl: FormControl = new FormControl<string[]>([]);
  public birthDateControl: FormControl = new FormControl<Date | null>(null);
  public deathDateControl: FormControl = new FormControl<Date | null>(null);
  public maritalStatusUidControl: FormControl = new FormControl<string[]>([]);
  public raceUidsControl: FormControl = new FormControl<string[]>([]);
  public ethnicityUidsControl: FormControl = new FormControl<string[]>([]);
  public preferredLanguageUidControl: FormControl = new FormControl<string[]>([]);
  public translatorRequiredControl: FormControl = new FormControl<boolean>(false);
  public dominantHandUidControl: FormControl = new FormControl<string[]>([]);
  public socialSecurityNumberControl: FormControl = new FormControl<string>('');
  public driversLicenseNumberControl: FormControl = new FormControl<string>('');
  public driversLicenseStateControl: FormControl = new FormControl<string>('');

  public listItemDefaultValidation: FormGroupValidationRules = {
    DriverLicenseNumber: {
      MaxLength: 25,
    },
    DriverLicenseState: {
      MaxLength: 3,
    },
    SocialSecurityNumber: {
      MaxLength: 11,
    },
  };

  constructor(injector: Injector) {
    super(injector);

    this.birthDateControl.valueChanges.pipe(
      distinctUntilChanged(),
      this.takeUntilDestroy(),
    ).subscribe((birthDate) => {
      this.deathMinDate = birthDate instanceof Date ? birthDate : undefined;
    });
    this.deathDateControl.valueChanges.pipe(
      distinctUntilChanged(),
      this.takeUntilDestroy(),
    ).subscribe((deathDate) => {
      const now = new Date();
      if (deathDate instanceof Date) {
        this.birthMaxDate = new Date(Math.min(now.getTime(), deathDate.getTime()));
      } else {
        this.birthMaxDate = now;
      }
    });

    // UpperCase string for FormControl
    this.formControlToUpperCase(this.driversLicenseStateControl);
  }

  public ngOnChanges(changes: SimpleChanges): void {
    if (changes.itemContext != null) {
      this.fcListValidation.initValidation(this.itemContext, this.listItemDefaultValidation);
    }
    if (changes.form != null) {
      this.addControlsToForm();
    }
    if (changes.itemContext != null || changes.form != null || changes.listItem != null) {
      this.setForm();
    }
  }

  public setForm(): void {
    // Current Sex FindControl
    this.genderUidControl.setValidators(this.fcListValidation.getValidators('GenderUid'));
    this.genderUidControl.setValue(this.listItem?.GenderUid ? [this.listItem?.GenderUid] : []);

    // Birth Sex FindControl
    this.birthGenderUidControl.setValidators(this.fcListValidation.getValidators('BirthGenderUid'));
    this.birthGenderUidControl.setValue(this.listItem?.BirthGenderUid ? [this.listItem?.BirthGenderUid] : []);

    // Gender Identity FindControl
    this.genderIdentityUidControl.setValidators(this.fcListValidation.getValidators('GenderIdentityUid'));
    this.genderIdentityUidControl.setValue(this.listItem?.GenderIdentityUid ? [this.listItem?.GenderIdentityUid] : []);

    // Sexual Orientation FindControl
    this.sexualOrientationUidControl.setValidators(this.fcListValidation.getValidators('SexualOrientationUid'));
    this.sexualOrientationUidControl.setValue(this.listItem?.SexualOrientationUid ? [this.listItem?.SexualOrientationUid] : []);

    // Birth Date Control
    this.birthDateControl.setValidators(this.fcListValidation.getValidators('BirthDate'));
    this.setDateToControl(this.birthDateControl, this.listItem?.BirthDate ?? null, true);

    // Death Date Control
    this.deathDateControl.setValidators(this.fcListValidation.getValidators('DeathDate'));
    this.setDateToControl(this.deathDateControl, this.listItem?.DeathDate ?? null, true);

    // Marital Status FindControl
    this.maritalStatusUidControl.setValidators(this.fcListValidation.getValidators('MaritalStatusUid'));
    this.maritalStatusUidControl.setValue(this.listItem?.MaritalStatusUid ? [this.listItem?.MaritalStatusUid] : []);

    // Race FindControl
    this.raceUidsControl.setValidators(this.fcListValidation.getValidators('RaceUids'));
    this.raceUidsControl.setValue(this.listItem?.RaceUids ? this.listItem?.RaceUids : []);

    // Ethnicity FindControl
    this.ethnicityUidsControl.setValidators(this.fcListValidation.getValidators('EthnicityUids'));
    this.ethnicityUidsControl.setValue(this.listItem?.EthnicityUids ? this.listItem?.EthnicityUids : []);

    // Preferred Language FindControl
    this.preferredLanguageUidControl.setValidators(this.fcListValidation.getValidators('LanguageUid'));
    this.preferredLanguageUidControl.setValue(this.listItem?.LanguageUid ? [this.listItem.LanguageUid] : []);

    // Translator Required Checkbox
    this.translatorRequiredControl.setValue(this.listItem.TranslatorRequired ?? false);

    // Dominant Hand FindControl
    this.dominantHandUidControl.setValidators(this.fcListValidation.getValidators('DominantHandUid'));
    this.dominantHandUidControl.setValue(this.listItem?.DominantHandUid ? [this.listItem.DominantHandUid] : []);

    // Driver License Number Control
    this.driversLicenseNumberControl.setValidators(this.fcListValidation.getValidators('DriverLicenseNumber'));
    this.driversLicenseNumberControl.setValue(this.listItem?.DriverLicenseNumber ?? '');

    // Driver License State Control
    this.driversLicenseStateControl.setValidators(this.fcListValidation.getValidators('DriverLicenseState'));
    this.driversLicenseStateControl.setValue(this.listItem?.DriverLicenseState ?? '');

    // Social Security Number Control
    this.socialSecurityNumberControl.setValidators(this.fcListValidation.getValidators('SocialSecurityNumber'));
    this.socialSecurityNumberControl.setValue(this.listItem?.SocialSecurityNumber ?? '');
  }

  public addControlsToForm(): void {
    this.form.addControl('genderUid', this.genderUidControl);
    this.form.addControl('birthGenderUid', this.birthGenderUidControl);
    this.form.addControl('genderIdentityUid', this.genderIdentityUidControl);
    this.form.addControl('sexualOrientationUid', this.sexualOrientationUidControl);
    this.form.addControl('birthDate', this.birthDateControl);
    this.form.addControl('deathDate', this.deathDateControl);
    this.form.addControl('maritalStatusUid', this.maritalStatusUidControl);
    this.form.addControl('raceUids', this.raceUidsControl);
    this.form.addControl('ethnicityUids', this.ethnicityUidsControl);
    this.form.addControl('preferredLanguageUid', this.preferredLanguageUidControl);
    this.form.addControl('translatorRequired', this.translatorRequiredControl);
    this.form.addControl('dominantHandUid', this.dominantHandUidControl);
    this.form.addControl('socialSecurityNumber', this.socialSecurityNumberControl);
    this.form.addControl('driversLicenseNumber', this.driversLicenseNumberControl);
    this.form.addControl('driversLicenseState', this.driversLicenseStateControl);
  }

  public setDateToControl(control: FormControl, date?: Date | string | number | null, allowEmpty: boolean = false) {
    if (date != null) {
      date = date instanceof Date ? date : new Date(date);
      if (date.toString() !== 'Invalid Date') {
        control.setValue(date);
      } else {
        control.setValue(allowEmpty ? null : new Date());
      }
    } else {
      control.setValue(allowEmpty ? null : new Date());
    }
  }

  private formControlToUpperCase(formControl: FormControl<string>): void {
    formControl.valueChanges.pipe(this.takeUntilDestroy()).subscribe((value) => {
      if (typeof value !== 'string') return;
      const upperCaseString = value.toUpperCase();
      if (upperCaseString !== value) {
        formControl.setValue(upperCaseString, {emitEvent: false});
      }
    });
  }
}
